import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import styles from "./Analyse.module.css";
import ChatBar from "../../Components/Analyse/ChatBar/ChatBar";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Store";
import CompactDiagnosticList from "../../Components/Analyse/CompactDiagnosticList/CompactDiagnosticList";
import { Category } from "../../Components/Utils/SelectionPane/SelectionPane";
import CompactFilters, { ISelectOption } from "../../Components/Analyse/CompactFilters/CompactFilters";
import { dateRangeValue, namesToSortedOptions } from "../../Components/Analyse/CompactFilters/filterUtils";
import FilterSearchBar from "../../Components/SearchBar/FilterSearchBar/FilterSearchBar";
import SelectedDiagnosticsCounter from "../../Components/Analyse/SelectedDiagnosticsCounter/SelectedDiagnosticsCounter";
import AnalyseSectionsDrawer from "../../Components/Analyse/AnalyseSectionsDrawer/AnalyseSectionsDrawer";
import AnalyseMessageList from "../../Components/Analyse/AnalyseMessageList/AnalyseMessageList";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CurateResource } from "../../Types/Curate/CurateResource";
import ResourceViewer from "../../Components/Utils/ResourceViewer/ResourceViewer";
import NoItems from "../../Components/Utils/NoItems/NoItems";
import {
  fetchPrompts,
  fetchAnalyseSetup,
  updateActivitiesAction,
  sendAnalyseMcpMessage,
  setChatId,
  setFreePrompt,
  setSelectedOrganisations,
  setSelectedDepartments,
  setSelectedGroups,
  setSelectedTemplateTypes,
  changeCategory,
  archiveChat,
  deleteChat,
  deleteAllMyChats,
  archiveAllMyChats,
  fetchArchivedChats,
  unarchiveChat,
  changeImproveCategory,
  changeDevelopCategory,
  setSelectedEvidenceIds,
  selectEvidenceIds,
  removeEvidenceFile,
  swapEvidenceToLibrary,
  replaceChatEvidence,
  addUploadedEvidence,
  addLibraryEvidenceRefs,
  setUploadingEvidence,
  applyChatSummary,
  dismissFailedTurn,
} from "../../Redux/Store/Slices/analyse";
import {
  GetEmptyChat,
  GetAnalyseChatSummary,
  searchAnalyseChats,
  ChatSearchResult,
  getAnalyseLinkedEvidence,
  deleteAnalyseFileLink,
} from "../../API/Analyse/Analyse";
import { ContextMenuItem } from "../../Types/ContextMenuItem";
import { getClient, getClientHasThirdPartySubsets, getDevelopEnabled } from "../../Redux/Store/Slices/client";
import { IDrawerSection } from "../../Types/Drawer/ISectionsDrawerItem";
import PillsContainer, { PillsContainerItem } from "../../Components/Utils/PillsContainer/PillsContainer";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import EvidenceSourceSelector from "../../Components/Utils/EvidenceSourceSelector/EvidenceSourceSelector";
import {
  deleteConversationFile,
  getConversationFiles,
  uploadAnalyseConversationFile,
} from "../../API/ConversationFiles/ConversationFiles";
import { FileData } from "../../Components/Utils/FileItem/FileItem";
import { SavedToLibraryItem } from "../../Components/Utils/AddEvidencePopup/AddEvidencePopup";
import { cancelMcpChat } from "../../API/McpChat/McpChat";
import { LibraryFileRef } from "../../Types/Library/Library";
import { createPortal } from "react-dom";
import LoadingWrapper from "../../Components/Utils/Loading/LoadingWrapper";
import ConfirmAction from "../../Components/Utils/ConfirmAction/ConfirmAction";
import { MCP_AUDIENCE, useMcpWarmup } from "../../Hooks/useMcpWarmup";

const attachChatLinkIfMissing = (section: IDrawerSection): IDrawerSection => {
  if (section.items && section.items.length > 0) {
    return section;
  }

  return {
    ...section,
    items: [
      {
        id: section.id,
        title: section.title,
        url: `/chat/${section.id}`,
        checked: false,
      },
    ],
  };
};

const applyChatSearchOrder = (
  sections: IDrawerSection[],
  results: ChatSearchResult[] | null
): IDrawerSection[] => {
  if (!results || results.length === 0) {
    return sections.map((section) =>
      section.searchRelevance
        ? { ...section, searchRelevance: undefined }
        : section
    );
  }

  const byId = new Map(sections.map((section) => [section.id, section]));
  const ranked: IDrawerSection[] = [];
  const seen = new Set<string>();

  for (const result of results) {
    const section = byId.get(result.contextId);
    if (!section || seen.has(section.id)) continue;
    seen.add(section.id);
    ranked.push({ ...section, searchRelevance: result.relevance });
  }

  for (const section of sections) {
    if (seen.has(section.id)) continue;
    ranked.push({ ...section, searchRelevance: undefined });
  }

  return ranked;
};

/**
 * Owns the freePrompt subscription so typing only re-renders the text area,
 * not the whole Analyse page.
 */
const AnalyseChatBar = ({
  editable,
  isStreaming,
  onSend,
  onStop,
}: {
  editable: boolean;
  isStreaming: boolean;
  onSend: (value: string | undefined) => void;
  onStop: () => void;
}) => {
  const dispatch = useAppDispatch();
  const freePrompt = useAppSelector((state) => state.analyse.freePrompt);

  return (
    <ChatBar
      editable={editable}
      isStreaming={isStreaming}
      value={freePrompt}
      onChange={(value) => dispatch(setFreePrompt(value ?? ""))}
      onSend={onSend}
      onStop={onStop}
      placeholder={editable || isStreaming ? "Ask me anything..." : ""}
    />
  );
};

const Analyse = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();

  const { chatId: paramChatId } = useParams<{ chatId: string }>();
  const queryChatId = searchParams.get("chatId")?.trim() || undefined;
  // Prefer path param; accept legacy/query ?chatId= and canonicalize below.
  const routeChatId = paramChatId ?? queryChatId;
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  // Narrow, per-field subscriptions. Subscribing to the whole analyse slice made every
  // streamed token and every keystroke re-render the entire page. Fields that change on
  // each stream delta (messages content, progressSteps, freePrompt) are owned by
  // AnalyseMessageList / the chat bar wrapper below, not this component.
  const chatId = useAppSelector((state) => state.analyse.chatId);
  const messagesCount = useAppSelector((state) => state.analyse.messages.length);
  const chats = useAppSelector((state) => state.analyse.chats);
  const archivedChats = useAppSelector((state) => state.analyse.archivedChats);
  const selectedOrganisations = useAppSelector((state) => state.analyse.selectedOrganisations);
  const selectedDepartments = useAppSelector((state) => state.analyse.selectedDepartments);
  const selectedGroups = useAppSelector((state) => state.analyse.selectedGroups);
  const selectedTemplateTypes = useAppSelector((state) => state.analyse.selectedTemplateTypes);
  const categories = useAppSelector((state) => state.analyse.categories);
  const improveCategories = useAppSelector((state) => state.analyse.improveCategories);
  const developCategories = useAppSelector((state) => state.analyse.developCategories);
  const selectedChat = useAppSelector((state) => state.analyse.selectedChat);
  const isStreaming = useAppSelector((state) => state.analyse.isStreaming);
  const isAwaitingRemoteResponse = useAppSelector((state) => state.analyse.isAwaitingRemoteResponse);
  const isLoadingMessages = useAppSelector((state) => state.analyse.isLoadingMessages);
  const isDeletingChat = useAppSelector((state) => state.analyse.isDeletingChat);
  const isArchivingChats = useAppSelector((state) => state.analyse.isArchivingChats);
  const lastSendFailure = useAppSelector((state) => state.analyse.lastSendFailure);
  const uploadedEvidence = useAppSelector((state) => state.analyse.uploadedEvidence);
  const selectedEvidenceIds = useAppSelector((state) => state.analyse.selectedEvidenceIds);
  const uploadingEvidence = useAppSelector((state) => state.analyse.uploadingEvidence);

  const client = useAppSelector(getClient);
  const hasThirdPartySubsets = useAppSelector(getClientHasThirdPartySubsets);
  const aiPowered = useAppSelector((state) => state.client.aiPowered);
  const developEnabled = useAppSelector(getDevelopEnabled);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedDevelopObservees, setSelectedDevelopObservees] = React.useState<ISelectOption[]>([]);
  const [selectedDevelopOwners, setSelectedDevelopOwners] = React.useState<ISelectOption[]>([]);
  const [completedDate, setCompletedDate] = React.useState<ISelectOption[]>([]);
  const [chatSearchQuery, setChatSearchQuery] = React.useState('');
  const [chatSearchResults, setChatSearchResults] = React.useState<ChatSearchResult[] | null>(null);
  const [chatSearchLoading, setChatSearchLoading] = React.useState(false);
  const [chatSearchError, setChatSearchError] = React.useState<string | null>(null);
  const [evidenceCollapsed, setEvidenceCollapsed] = useState(false);
  const [contextCollapsed, setContextCollapsed] = useState(false);
  const [summaryPopup, setSummaryPopup] = useState<{ title: string; summary: string } | null>(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [confirmDeleteAllChats, setConfirmDeleteAllChats] = useState(false);
  const [confirmArchiveAllChats, setConfirmArchiveAllChats] = useState(false);
  const chatHadMessagesRef = useRef(false);
  const chatSearchAbortRef = useRef<AbortController | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);

  const sectionsWithChatLinks = useMemo(
    () => applyChatSearchOrder(chats.map(attachChatLinkIfMissing), chatSearchResults),
    [chats, chatSearchResults]
  );
  const archivedSectionsWithChatLinks = useMemo(() => archivedChats.map(attachChatLinkIfMissing), [archivedChats]);

  const viewedChatId = routeChatId ?? chatId;
  useMcpWarmup(viewedChatId);
  const activeChatSearchHighlights = useMemo(() => {
    if (!viewedChatId || !chatSearchResults?.length) {
      return null;
    }

    const match = chatSearchResults.find(
      (result) => result.contextId === viewedChatId && result.relevance === "high"
    );
    if (!match) {
      return null;
    }

    const phrases = match.matchedTexts?.filter((text) => text.trim().length >= 2) ?? [];
    if (phrases.length > 0) {
      return phrases;
    }

    // Fallback when the agent omitted matchedTexts: highlight meaningful query tokens.
    return chatSearchQuery
      .trim()
      .split(/\s+/)
      .map((token) => token.replace(/[^a-zA-Z0-9-]+/g, ""))
      .filter((token) => token.length >= 3);
  }, [viewedChatId, chatSearchResults, chatSearchQuery]);

  useEffect(() => {
    const query = chatSearchQuery.trim();
    if (!query) {
      chatSearchAbortRef.current?.abort();
      chatSearchAbortRef.current = null;
      setChatSearchResults(null);
      setChatSearchLoading(false);
      setChatSearchError(null);
      return;
    }

    if (!client?.id) {
      return;
    }

    const timer = window.setTimeout(async () => {
      chatSearchAbortRef.current?.abort();
      const controller = new AbortController();
      chatSearchAbortRef.current = controller;
      setChatSearchLoading(true);
      setChatSearchError(null);

      try {
        const response = await searchAnalyseChats(
          client.id,
          query,
          controller.signal
        );
        if (controller.signal.aborted) return;
        setChatSearchResults(response.results);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error(error);
        setChatSearchError("Could not search chats.");
        setChatSearchResults(null);
      } finally {
        if (!controller.signal.aborted) {
          setChatSearchLoading(false);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timer);
    };
  }, [chatSearchQuery, client?.id]);

  useEffect(() => {
    return () => {
      chatSearchAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (aiPowered === false) {
      navigate("/", { replace: true });
    }
  }, [aiPowered, navigate]);
  
  const diagnosticsSectionRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const newChatRequestIdRef = useRef(0);
  const skipActivitySyncRef = useRef(true);
  const lastSyncedActivityKeyRef = useRef<string>("");

  useEffect(() => {
    if (!settingsMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setSettingsMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSettingsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [settingsMenuOpen]);

  useEffect(() => {
    if (!activeChatSearchHighlights?.length || messagesCount === 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      const highlight = messagesContainerRef.current?.querySelector(
        ".analyse-search-highlight"
      ) as HTMLElement | null;
      highlight?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [activeChatSearchHighlights, messagesCount, viewedChatId]);

  const newChatHandler = useCallback(async () => {
    if (aiPowered === false) {
      return;
    }

    const requestId = ++newChatRequestIdRef.current;

    if (!client?.id || client.id === 'undefined' || client.id === 'null') {
      // Wait for client — do not invent a random chat while bootstrap is incomplete.
      return;
    }

    const emptyChatId = await GetEmptyChat(client.id);
    // A later route-driven setChatId / New Chat click cancels this navigation.
    if (requestId !== newChatRequestIdRef.current) {
      return;
    }

    if (emptyChatId) {
      navigate(`/analyse/chat/${emptyChatId}`);
    } else {
      navigate("/analyse/chat/" + crypto.randomUUID());
    }
  }, [aiPowered, client?.id, navigate]);

  useEffect(() => {
    if (aiPowered === false) {
      return;
    }

    // Legacy/misbuilt links used /analyse?chatId=... which never matches :chatId.
    if (!paramChatId && queryChatId) {
      newChatRequestIdRef.current += 1;
      navigate(`/analyse/chat/${queryChatId}`, { replace: true });
      return;
    }

    if (!routeChatId) {
      newChatHandler();
    } else {
      // Cancel any in-flight GetEmptyChat navigation so it cannot overwrite this chat.
      newChatRequestIdRef.current += 1;
      skipActivitySyncRef.current = true;
      lastSyncedActivityKeyRef.current = "";
      dispatch(setChatId(routeChatId));
    }
  }, [aiPowered, paramChatId, queryChatId, routeChatId, dispatch, newChatHandler, navigate]);

  useEffect(() => {
    dispatch(fetchPrompts());
  }, [dispatch]);

  // Fetch archived chats first
  useEffect(() => {
    if (user?.sub && client?.id && client.id !== "undefined" && client.id !== "null") {
      dispatch(fetchArchivedChats({
        clientId: client.id,
        userSub: user.sub
      }));
    }
  }, [client?.id, dispatch, user?.sub]);

  // Then fetch the specific chat setup
  useEffect(() => {
    if (user?.sub && routeChatId && client?.id && client.id !== 'undefined' && client.id !== 'null') {
      skipActivitySyncRef.current = true;
      dispatch(
        fetchAnalyseSetup({
          chatId: routeChatId,
          clientId: client.id,
          userSub: user.sub,
          selectedOrgs: [],
          selectedDeps: [],
          selectedGroups: [],
        })
      );
    }
  }, [user?.sub, routeChatId, client?.id, dispatch]);

  // While Mia is still finishing a reply after refresh/navigation, poll until it clears.
  useEffect(() => {
    if (
      !isAwaitingRemoteResponse ||
      isStreaming ||
      !user?.sub ||
      !routeChatId ||
      !client?.id ||
      client.id === "undefined" ||
      client.id === "null"
    ) {
      return;
    }

    const poll = () => {
      dispatch(
        fetchAnalyseSetup({
          chatId: routeChatId,
          clientId: client.id,
          userSub: user.sub!,
          selectedOrgs: [],
          selectedDeps: [],
          selectedGroups: [],
          quiet: true,
        })
      );
    };

    const intervalId = window.setInterval(poll, 2500);
    return () => window.clearInterval(intervalId);
  }, [
    isAwaitingRemoteResponse,
    isStreaming,
    user?.sub,
    routeChatId,
    client?.id,
    dispatch,
  ]);

  /*const categoryChanged = (category: Category<string>) => {
    const newCategories = categories.map((cat) =>
      cat.title === category.title ? category : cat
    );
    dispatch(changeCategory(category));
    const activityIds = newCategories
      .flatMap((cat) => cat.data.filter((i) => i.selected).map((i) => i.id));
    if (selectedChat?.id) {
      dispatch(
        updateActivitiesAction({ chatId: selectedChat.id, activityIds })
      );
    }
  };*/

  const categoryChanged = useCallback(
      (category: Category<string>) => {
        dispatch(changeCategory(category));
      },
      [dispatch]
  );

    const improveCategoryChanged = useCallback(
      (category: Category<string>) => {
        dispatch(changeImproveCategory(category));
      },
      [dispatch]
  );

  const developCategoryChanged = useCallback(
    (category: Category<string>) => {
      dispatch(changeDevelopCategory(category));
    },
    [dispatch]
  );


  const selectedActivityIds = useMemo(
      () => {
          const fromCategories = categories.flatMap(cat =>
              cat.data.filter(i => i.selected).map(i => i.id)
          ).concat(
            improveCategories.flatMap(cat =>
              cat.data.filter(i => i.selected).map(i => i.id)
            )
          ).concat(
            (developEnabled ? developCategories : []).flatMap(cat =>
              cat.data.filter(i => i.selected).map(i => i.id)
            )
          );

          if (fromCategories.length > 0) {
            return fromCategories;
          }

          // Fallback when catalogue page doesn't include previously selected assets.
          return selectedChat?.selectedActivityIds ?? [];
      },
      [categories, improveCategories, developCategories, developEnabled, selectedChat?.selectedActivityIds]
  );

  const visibleDevelopCategories = useMemo(
    () => (developEnabled ? developCategories : []),
    [developEnabled, developCategories]
  );

  useEffect(() => {
    if (!developEnabled) {
      setSelectedDevelopObservees([]);
      setSelectedDevelopOwners([]);
      setCompletedDate([]);
    }
  }, [developEnabled]);

  const developObserveeOptions = useMemo<ISelectOption[]>(() => {
    const names: string[] = [];
    for (const cat of visibleDevelopCategories) {
      for (const item of cat.data) {
        const withNames = item as { observee?: string; individual?: string };
        names.push(withNames.observee || withNames.individual || "");
      }
    }
    return namesToSortedOptions(names);
  }, [visibleDevelopCategories]);

  const developOwnerOptions = useMemo<ISelectOption[]>(() => {
    const names: string[] = [];
    for (const cat of visibleDevelopCategories) {
      for (const item of cat.data) {
        names.push((item as { ownerName?: string }).ownerName || "");
      }
    }
    return namesToSortedOptions(names);
  }, [visibleDevelopCategories]);

  const allVisibleCategories = useMemo(
    () => categories.concat(improveCategories, visibleDevelopCategories),
    [categories, improveCategories, visibleDevelopCategories]
  );

  const totalSelectedAssets = selectedActivityIds.length;
  const selectedEvidenceFiles = useMemo(
    () => uploadedEvidence.filter((file) => selectedEvidenceIds.includes(file.id)),
    [uploadedEvidence, selectedEvidenceIds]
  );
  const hasEvidence = selectedEvidenceIds.length > 0;

  const currentChatId = routeChatId ?? chatId;

  const analyseFilterConfig = useMemo(() => {
    const base = [
      { key: 'organisations', label: 'Organisations', placeholder: 'Select organisations', dataSource: 'api' as const },
      { key: 'departments', label: 'Departments', placeholder: 'Select departments', dataSource: 'api' as const },
      { key: 'groups', label: 'Groups', placeholder: 'Select groups', dataSource: 'api' as const },
      { key: 'templateTypes', label: 'Types', placeholder: 'Select types', dataSource: 'props' as const, data: [] },
    ];
    if (!developEnabled) {
      return base;
    }
    // Develop-only, like Completed For / Completed By: only Develop items carry
    // a completion date, and the "no date => excluded" rule would otherwise grey
    // out every Evaluate and Track item the moment a bound is set.
    return [
      { key: 'observees', label: 'Completed For', placeholder: 'Select users', dataSource: 'props' as const, data: developObserveeOptions },
      { key: 'owners', label: 'Completed By', placeholder: 'Select users', dataSource: 'props' as const, data: developOwnerOptions },
      { key: 'completedDate', label: 'Date Completed', placeholder: 'Pick a date range', kind: 'dateRange' as const },
      ...base,
    ];
  }, [developEnabled, developObserveeOptions, developOwnerOptions]);

  const handleAnalyseFilterChange = useCallback((filters: Record<string, ISelectOption[]>) => {
    if (developEnabled) {
      setSelectedDevelopObservees(filters.observees || []);
      setSelectedDevelopOwners(filters.owners || []);
      setCompletedDate(filters.completedDate || []);
    }
    dispatch(setSelectedOrganisations(filters.organisations || []));
    dispatch(setSelectedDepartments(filters.departments || []));
    dispatch(setSelectedGroups(filters.groups || []));
    dispatch(setSelectedTemplateTypes(filters.templateTypes || []));
  }, [developEnabled, dispatch]);

  const analyseSelectedFilters = useMemo(() => ({
    ...(developEnabled
      ? {
          observees: selectedDevelopObservees,
          owners: selectedDevelopOwners,
          completedDate,
        }
      : {}),
    organisations: hasThirdPartySubsets ? selectedOrganisations : [],
    departments: hasThirdPartySubsets ? selectedDepartments : [],
    groups: selectedGroups,
    templateTypes: selectedTemplateTypes,
  }), [developEnabled, selectedDevelopObservees, selectedDevelopOwners, completedDate, hasThirdPartySubsets, selectedOrganisations, selectedDepartments, selectedGroups, selectedTemplateTypes]);

  const completedRange = useMemo(() => dateRangeValue(completedDate), [completedDate]);

  useEffect(() => {
    if (!selectedChat?.id) return;

    // Skip the first sync after setup load — categories are hydrated from the
    // server and must not immediately POST [] (or a stale selection) back.
    if (skipActivitySyncRef.current) {
      skipActivitySyncRef.current = false;
      lastSyncedActivityKeyRef.current = `${selectedChat.id}:${selectedActivityIds.join(",")}`;
      return;
    }

    const syncKey = `${selectedChat.id}:${selectedActivityIds.join(",")}`;
    if (syncKey === lastSyncedActivityKeyRef.current) {
      return;
    }
    lastSyncedActivityKeyRef.current = syncKey;

    dispatch(
        updateActivitiesAction({
          chatId: selectedChat.id,
          activityIds: selectedActivityIds,
        })
    );
  }, [dispatch, selectedChat?.id, selectedActivityIds]);

  // Scroll to diagnostics section when a prompt is selected
  useEffect(() => {
    if (selectedChat?.promptId && diagnosticsSectionRef.current) {
      setTimeout(() => {
        diagnosticsSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100); // Small delay to ensure the section is rendered
    }
  }, [selectedChat?.promptId]);

  // Scroll to bottom when messages change (after new message is rendered)
  useEffect(() => {
    if (messagesCount > 0) {
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 200); // Longer delay to ensure new content is fully rendered
    }
  }, [messagesCount]);

  // Evidence + context panels: expanded for empty chats; collapse after the first prompt (or when opening a chat with history)
  useEffect(() => {
    const hasMessages = messagesCount > 0;
    setEvidenceCollapsed(hasMessages);
    setContextCollapsed(hasMessages);
    chatHadMessagesRef.current = hasMessages;
    // Runs only when switching chats; message-count changes are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChatId]);

  useEffect(() => {
    const hasMessages = messagesCount > 0;
    if (hasMessages && !chatHadMessagesRef.current) {
      setEvidenceCollapsed(true);
      setContextCollapsed(true);
    }
    if (!hasMessages) {
      setEvidenceCollapsed(false);
      setContextCollapsed(false);
    }
    chatHadMessagesRef.current = hasMessages;
  }, [messagesCount]);

  const getSendMessageState = () => {
    const isCurrentChatArchived =
      !!currentChatId && archivedChats.some((c) => c.id === currentChatId);

    const isActiveChat =
      !!currentChatId && chats.some((c) => c.id === currentChatId);

    const hasPrompt = !!selectedChat?.promptId;
    const hasDiagnostics = selectedActivityIds.length > 0;
    const hasContext = hasDiagnostics || hasEvidence;

    const canSendMessage =
      !!currentChatId &&
      isActiveChat &&
      !isCurrentChatArchived &&
      !isStreaming &&
      !isAwaitingRemoteResponse &&
      !isLoadingMessages &&
      !isDeletingChat &&
      !isArchivingChats;

    return {
      currentChatId,
      canSendMessage,
      isCurrentChatArchived,
      isActiveChat,
      hasPrompt,
      hasDiagnostics,
      hasEvidence,
      hasContext,
    };
  };

  const { canSendMessage } = getSendMessageState();

  // Prompt text arrives from the chat bar's onSend; this component no longer
  // subscribes to freePrompt, so keystrokes don't re-render the page.
  const getAnalyseResponse = async (promptText?: string) => {
    const { currentChatId, canSendMessage } = getSendMessageState();
    if (!canSendMessage) return;

    const prompt = promptText?.trim() ?? "";
    if (!prompt) {
      toast.warn("Please type a prompt before sending.");
      return;
    }

    if (!client?.id) {
      toast.error("Client information is not available.");
      return;
    }

    const controller = new AbortController();
    streamAbortRef.current = controller;

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: MCP_AUDIENCE },
      });

      await dispatch(
        sendAnalyseMcpMessage({
          token,
          clientId: client.id,
          chatId: currentChatId,
          freePrompt: prompt,
          categories,
          improveCategories,
          developCategories: visibleDevelopCategories,
          evidenceFiles: selectedEvidenceFiles,
          signal: controller.signal,
        })
      ).unwrap();
    } catch {
      // Error / cancel is shown inline on the assistant bubble.
    } finally {
      if (streamAbortRef.current === controller) {
        streamAbortRef.current = null;
      }
    }
  };

  const stopAnalyseResponse = async () => {
    const controller = streamAbortRef.current;
    controller?.abort();
    const currentChatId = chatId;
    if (!currentChatId) {
      return;
    }
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: MCP_AUDIENCE },
      });
      await cancelMcpChat(token, currentChatId);
    } catch {
      // Client abort already stopped the stream; server cancel is best-effort.
    }
  };

  const retryFailedTurn = async () => {
    const failed = lastSendFailure;
    if (!failed) {
      return;
    }
    dispatch(dismissFailedTurn());
    await getAnalyseResponse(failed.prompt);
  };

  const refreshChatEvidence = useCallback(async () => {
    if (!client?.id || !currentChatId) {
      return;
    }

    try {
      const [linkedEvidence, conversationFiles] = await Promise.all([
        getAnalyseLinkedEvidence(currentChatId),
        getConversationFiles(currentChatId, client.id),
      ]);
      dispatch(replaceChatEvidence({ chatId: currentChatId, linkedEvidence, conversationFiles }));
    } catch (err) {
      console.error("Error refreshing chat evidence:", err);
    }
  }, [client?.id, currentChatId, dispatch]);

  const handleEvidenceAdded = useCallback(
    async (linkedRefs: LibraryFileRef[]) => {
      dispatch(addLibraryEvidenceRefs(linkedRefs));
      // Must match addLibraryEvidenceRefs: prefer Cosmos fileId over path composite.
      const newIds = linkedRefs.map(
        (ref) =>
          ref.fileId?.trim() ||
          `${ref.portfolioId}|${ref.folderId}|${ref.fileName}`
      );
      dispatch(selectEvidenceIds(newIds));
      await refreshChatEvidence();
    },
    [dispatch, refreshChatEvidence]
  );

  const handleUploadedFilesSelected = useCallback(
    async (fileIds: string[]) => {
      dispatch(selectEvidenceIds(fileIds));
      await refreshChatEvidence();
    },
    [dispatch, refreshChatEvidence]
  );

  const handleSavedToLibrary = useCallback(
    async (saved: SavedToLibraryItem[]) => {
      if (saved.length === 0) return;
      dispatch(
        swapEvidenceToLibrary(
          saved.map((item) => ({
            uploadedFileId: item.uploadedFileId,
            wasSelected: selectedEvidenceIds.includes(item.uploadedFileId),
            library: {
              id: item.libraryRef.fileId?.trim() || `${item.libraryRef.portfolioId}|${item.libraryRef.folderId}|${item.libraryRef.fileName}`,
              fileName: item.libraryRef.fileName,
              portfolioId: item.libraryRef.portfolioId,
              folderId: item.libraryRef.folderId,
              source: "library" as const,
              linkId: item.linkId,
            },
          }))
        )
      );
      await refreshChatEvidence();
    },
    [dispatch, refreshChatEvidence, selectedEvidenceIds]
  );

  const [removingEvidenceId, setRemovingEvidenceId] = useState<string | null>(null);

  const handleRemoveEvidence = useCallback(
    async (file: FileData) => {
      if (!client?.id || !currentChatId || isStreaming) {
        return;
      }

      setRemovingEvidenceId(file.id);
      try {
        if (file.source === "uploaded") {
          await deleteConversationFile(file.id, client.id);
        } else {
          const linked = await getAnalyseLinkedEvidence(currentChatId);
          const linkIds = Array.from(
            new Set(
              linked
                .filter(
                  (item) =>
                    (!!file.linkId && item.linkId === file.linkId) ||
                    item.id === file.id ||
                    (!!file.portfolioId &&
                      item.portfolioId === file.portfolioId &&
                      item.folderId === file.folderId &&
                      item.fileName === file.fileName)
                )
                .map((item) => item.linkId)
                .filter((linkId): linkId is string => !!linkId)
            )
          );

          if (linkIds.length === 0) {
            toast.error("Unable to unlink this library file.");
            return;
          }

          await Promise.all(linkIds.map((linkId) => deleteAnalyseFileLink(currentChatId, linkId)));
        }

        dispatch(removeEvidenceFile(file.id));
        await refreshChatEvidence();
        toast.success(
          file.source === "uploaded" ? "Uploaded file deleted." : "Library file unlinked from this chat."
        );
      } catch (err) {
        console.error("Error removing evidence:", err);
        toast.error("Failed to remove evidence. Please try again.");
      } finally {
        setRemovingEvidenceId(null);
      }
    },
    [client?.id, currentChatId, isStreaming, dispatch, refreshChatEvidence]
  );

  const handleUploadEvidence = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList?.length || !client?.id || !currentChatId) {
        return;
      }

      const file = fileList[0];
      dispatch(setUploadingEvidence(true));

      try {
        const response = await uploadAnalyseConversationFile(
          file,
          client.id,
          currentChatId
        );

        const uploadedFile = {
          id: response.fileId,
          fileName: response.fileName,
          portfolioId: "",
          folderId: "",
          source: "uploaded" as const,
          conversationId: response.conversationId,
        };

        dispatch(addUploadedEvidence(uploadedFile));
        dispatch(selectEvidenceIds([response.fileId]));
        toast.success("File uploaded and ready for Mia.");
      } catch (err) {
        console.error("Error uploading evidence file:", err);
        toast.error("Failed to upload file. Please try again.");
      } finally {
        dispatch(setUploadingEvidence(false));
      }
    },
    [client?.id, currentChatId, dispatch]
  );

  // Stable identity so memoized message items don't re-render when this page does.
  const [viewerResource, setViewerResource] = useState<CurateResource | null>(null);

  const resourceClicked = useCallback((resource: CurateResource) => {
    setViewerResource(resource);
  }, []);

  const deleteChatHandler = useCallback(async (chatId: string) => {
    try {
      await dispatch(deleteChat(chatId)).unwrap();
      navigate("/analyse");
    } catch {
      // Toast handled in the slice.
    }
  }, [dispatch, navigate]);

  const deleteAllChatsHandler = useCallback(async () => {
    if (!client?.id || client.id === "undefined" || client.id === "null") {
      return;
    }

    streamAbortRef.current?.abort();
    chatSearchAbortRef.current?.abort();
    setChatSearchQuery("");
    setChatSearchResults(null);
    setChatSearchError(null);
    setSettingsMenuOpen(false);

    await dispatch(deleteAllMyChats(client.id)).unwrap();
    await newChatHandler();
  }, [client?.id, dispatch, newChatHandler]);

  const archiveAllChatsHandler = useCallback(async () => {
    if (!client?.id || client.id === "undefined" || client.id === "null") {
      return;
    }

    streamAbortRef.current?.abort();
    chatSearchAbortRef.current?.abort();
    setChatSearchQuery("");
    setChatSearchResults(null);
    setChatSearchError(null);
    setSettingsMenuOpen(false);

    await dispatch(archiveAllMyChats(client.id)).unwrap();
    await newChatHandler();
  }, [client?.id, dispatch, newChatHandler]);

  const archiveChatHandler = useCallback(async (chatId: string) => {
    dispatch(archiveChat(chatId))
    navigate("/analyse");
  }, [dispatch, navigate]);

  const unarchiveChatHandler = useCallback(async (chatId: string) => {
    dispatch(unarchiveChat(chatId))
  }, [dispatch]);

  const viewChatSummaryHandler = useCallback(
    async (sectionId: string) => {
      const section =
        chats.find((chat) => chat.id === sectionId) ??
        archivedChats.find((chat) => chat.id === sectionId);
      let title = section?.title?.trim() || "Chat summary";
      let summary =
        section?.chatSummary?.trim() ||
        (selectedChat?.id === sectionId ? selectedChat.chatSummary?.trim() : "") ||
        "";

      if (!summary) {
        try {
          const data = await GetAnalyseChatSummary(sectionId);
          summary = data.chatSummary?.trim() ?? "";
          if (data.title?.trim()) {
            title = data.title.trim();
          }
          if (summary) {
            dispatch(applyChatSummary({ chatId: sectionId, summary }));
          }
        } catch (error) {
          console.error("Failed to load chat summary:", error);
        }
      }

      setSummaryPopup({ title, summary });
    },
    [chats, archivedChats, selectedChat, dispatch]
  );

  const contextMenuItems: ContextMenuItem[] = useMemo(() => [
    {
      title: "View summary",
      onClickHandler: (sectionId) => viewChatSummaryHandler(sectionId),
      icon: "/img/sparkle-blue.svg",
    },
    {
      title: "Delete",
      onClickHandler: (sectionId) => deleteChatHandler(sectionId),
      icon: "/img/trash-red.svg",
      style: {
        backgroundColor: "#F0DFDE",
        color: "red",
      },
      requiresConfirmation: true,
      confirmationConfig: {
        title: "Confirm Delete",
        message: "Are you sure you want to delete this chat? This action cannot be undone.",
        severity: "danger",
        icon: "/img/trash-red.svg"
      }
    },
    {
      title: "Archive",
      onClickHandler: (sectionId) => archiveChatHandler(sectionId),
      icon: "/img/archive.svg",
      requiresConfirmation: true,
      confirmationConfig: {
        title: "Confirm Archive",
        message: "Are you sure you want to archive this chat?",
        severity: "warning",
        icon: "/img/archive.svg"
      }
    }
  ], [viewChatSummaryHandler, deleteChatHandler, archiveChatHandler]);

  const archivedContextMenuItems: ContextMenuItem[] = useMemo(() => [
    {
      title: "View summary",
      onClickHandler: (sectionId) => viewChatSummaryHandler(sectionId),
      icon: "/img/sparkle-blue.svg",
    },
    {
      title: "Delete",
      onClickHandler: (sectionId) => deleteChatHandler(sectionId),
      icon: "/img/trash-red.svg",
      style: {
        backgroundColor: "#F0DFDE",
        color: "red",
      },
      requiresConfirmation: true,
      confirmationConfig: {
        title: "Confirm Delete",
        message: "Are you sure you want to delete this chat? This action cannot be undone.",
        severity: "danger",
        icon: "/img/trash-red.svg"
      }
    },
    {
      title: "Unarchive",
      onClickHandler: (sectionId) => unarchiveChatHandler(sectionId),
      icon: "/img/archive.svg",
      requiresConfirmation: true,
      confirmationConfig: {
        title: "Confirm Unarchive",
        message: "Are you sure you want to unarchive this chat?",
        severity: "warning",
        icon: "/img/archive.svg"
      }
    }
  ], [viewChatSummaryHandler, deleteChatHandler, unarchiveChatHandler]);

  const getNextStepMessage = (): string | null => {
    const {
      isCurrentChatArchived,
      isActiveChat,
      currentChatId: stepChatId
    } = getSendMessageState();

    if (isCurrentChatArchived) {
      return "This chat is archived. Unarchive it to continue the conversation.";
    }

    // common during load / navigating to a chat id that hasn't populated chats[] yet
    if (isLoadingMessages || !stepChatId || !isActiveChat) {
      return "Loading chat…";
    }

    if (isDeletingChat) {
      return "Deleting chat…";
    }

    if (isArchivingChats) {
      return "Archiving chats…";
    }

    if (isAwaitingRemoteResponse) {
      return "Mia is still responding…";
    }

    return null;
  };

  const nextStepMessage = getNextStepMessage();

  const onPillClicked = (item: PillsContainerItem) => {
    dispatch(setFreePrompt(item.fullLabel ?? item.label))
  }

  const selectedDiagnosticChanged = useCallback(
    (category: Category<string>) => {
      if (categories.some((cat) => cat.title === category.title)) {
        categoryChanged(category);
        return;
      }

      if (improveCategories.some((cat) => cat.title === category.title)) {
        improveCategoryChanged(category);
        return;
      }

      if (visibleDevelopCategories.some((cat) => cat.title === category.title)) {
        developCategoryChanged(category);
      }
    },
    [
      categories,
      improveCategories,
      visibleDevelopCategories,
      categoryChanged,
      improveCategoryChanged,
      developCategoryChanged,
    ]
  );

  if (aiPowered !== true) {
    return null;
  }

  return (
    <div className={styles.container}>
      {(isDeletingChat || isArchivingChats) && (
        <div className={styles.bufferOverlay} role="status" aria-live="polite">
          <LoadingWrapper
            loading
            loadingMessage={isArchivingChats ? "Archiving chats…" : "Deleting chat…"}
          />
        </div>
      )}
      <section className={`${styles.left}`}>
        
        <div className={styles.chatWindow} data-testid="analyse-chat-window">
          <div className={styles.chatWindowContainer}>
            {!isLoadingMessages && messagesCount === 0 && (
              <div className={styles.heroCard}>
                <div className={styles.heroBadge}>Chat with Mia</div>
                <div className={styles.chatWindow__title}>
                  <h1 className={styles.userMessage}>Hello, {user?.givenName}</h1>
                  <p className={styles.promptMessage}>
                    Select the assets you want Mia to analyse across evaluation, tracking and development.
                  </p>
                </div>
              </div>
            )}

            <div
              ref={diagnosticsSectionRef}
              className={`${styles.selectData} ${contextCollapsed ? styles.selectDataCollapsed : ""}`}
            >
              <div className={styles.workspacePanel}>
                <div className={styles.chatWindow__select}>
                  <div
                    className={`${styles.workspacePanelHeader} ${styles.workspacePanelHeaderCollapsible}`}
                    onClick={() => setContextCollapsed((prev) => !prev)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={!contextCollapsed}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setContextCollapsed((prev) => !prev);
                      }
                    }}
                  >
                    <div>
                      <div className={styles.workspaceTitleRow}>
                        <img
                          src="/img/logo.png"
                          alt=""
                          className={styles.workspaceLogo}
                          aria-hidden
                        />
                        <h2 className={styles.workspaceTitle}>Context selection</h2>
                      </div>
                      {contextCollapsed ? (
                        <p className={styles.workspaceSubtitle}>
                          {totalSelectedAssets === 0
                            ? "No assets selected — expand to choose diagnostics and plans"
                            : `${totalSelectedAssets} asset${totalSelectedAssets === 1 ? "" : "s"} selected — expand to change`}
                        </p>
                      ) : (
                        <p className={styles.workspaceSubtitle}>
                          {messagesCount === 0
                            ? "Refine the evidence which Mia should use before you start the conversation."
                            : "Adjust which assets Mia can use for this conversation."}
                        </p>
                      )}
                    </div>
                    <div className={styles.workspaceMeta}>
                      <span>{totalSelectedAssets} selected</span>
                      <button
                        type="button"
                        className={`${styles.contextCollapseToggle} ${
                          contextCollapsed ? styles.contextCollapseToggleCollapsed : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextCollapsed((prev) => !prev);
                        }}
                        aria-label={contextCollapsed ? "Expand context selection" : "Collapse context selection"}
                        aria-expanded={!contextCollapsed}
                      >
                        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M2 4.5L6 8.5L10 4.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div
                    className={`${styles.contextBody} ${
                      contextCollapsed ? styles.contextBodyClosed : styles.contextBodyOpen
                    }`}
                    aria-hidden={contextCollapsed}
                  >
                    <div className={styles.contextBodyInner}>
                      <div className={styles.filtersAndSearch}>
                        <FilterSearchBar
                          value={searchTerm}
                          onChange={setSearchTerm}
                          filters={analyseFilterConfig}
                          selectedFilters={analyseSelectedFilters}
                          onFilterChange={handleAnalyseFilterChange}
                          placeholder="Search across all connected assets..."
                        />

                        <CompactFilters
                          filters={analyseFilterConfig}
                          selectedFilters={analyseSelectedFilters}
                          onFilterChange={handleAnalyseFilterChange}
                        />
                      </div>
                    
                      <div className={`${styles.dataSelectionLists} ${!developEnabled ? styles.dataSelectionListsTwoCol : ""}`}>
                      
                        <div className={`${styles.dataSelection} ${styles.dataSelectionEvaluate}`}>
                          <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>Evaluate</h2>
                            <p className={styles.sectionSubtitle}>Diagnostics and enquiry evidence</p>
                          </div>
                          {categories.length === 0 ? (
                            <NoItems message="No diagnostics available to analyse." />
                          ) : (
                            <CompactDiagnosticList
                              categories={categories}
                              searchTerm={searchTerm}
                              onChange={categoryChanged}
                              selectedOrganisations={selectedOrganisations}
                              selectedDepartments={selectedDepartments}
                              selectedGroups={selectedGroups}
                              selectedTemplateTypes={selectedTemplateTypes}
                            />
                          )}
                        </div>
                        <div className={`${styles.dataSelection} ${styles.dataSelectionTrack}`}>
                          <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>Track</h2>
                            <p className={styles.sectionSubtitle}>Improvement planning and progress</p>
                          </div>
                          {improveCategories.length === 0 ? (
                            <NoItems message="No plans available to analyse." />
                          ) : (
                            <CompactDiagnosticList
                              searchTerm={searchTerm}
                              categories={improveCategories}
                              onChange={improveCategoryChanged}
                              selectedOrganisations={selectedOrganisations}
                              selectedDepartments={selectedDepartments}
                              selectedGroups={selectedGroups}
                              selectedTemplateTypes={selectedTemplateTypes}
                            />
                          )}
                        </div>
                        {developEnabled && (
                        <div className={`${styles.dataSelection} ${styles.dataSelectionDevelop}`}>
                          <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>Develop</h2>
                            <p className={styles.sectionSubtitle}>Observation and coaching evidence</p>
                          </div>
                          {visibleDevelopCategories.length === 0 ? (
                            <NoItems message="No develop assets available to analyse." />
                          ) : (
                            <CompactDiagnosticList
                              searchTerm={searchTerm}
                              categories={visibleDevelopCategories}
                              onChange={developCategoryChanged}
                              selectedOrganisations={selectedOrganisations}
                              selectedDepartments={selectedDepartments}
                              selectedGroups={selectedGroups}
                              selectedTemplateTypes={selectedTemplateTypes}
                              selectedObservees={selectedDevelopObservees}
                              selectedOwners={selectedDevelopOwners}
                              completedFrom={completedRange.from}
                              completedTo={completedRange.to}
                            />
                          )}
                        </div>
                        )}




                        </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div ref={messagesContainerRef} className={styles.chatWindow__messages}>
            {/* Owns messages/progress subscriptions so per-token updates don't re-render this page. */}
            <AnalyseMessageList
              highlightPhrases={activeChatSearchHighlights}
              resourceClicked={resourceClicked}
              onRetryFailedTurn={retryFailedTurn}
            />
          </div>
        </div>
        <div className={styles.chatBar}>
          {!!nextStepMessage && (
            <div className={styles.chatBar__promptRequired}>
              <p>{nextStepMessage}</p>
            </div>
          )}
          <EvidenceSourceSelector
            files={uploadedEvidence}
            loading={false}
            selectedIds={selectedEvidenceIds}
            onSelectionChange={(ids) => dispatch(setSelectedEvidenceIds(ids))}
            onRemoveEvidence={handleRemoveEvidence}
            removingFileId={removingEvidenceId ?? (isStreaming ? "streaming" : null)}
            canLinkEvidence
            roleName="user"
            title="Evidence for Mia"
            hint="Upload files or link from your library. Evidence you add will be readable by Mia. The X unlinks a library file or deletes an uploaded file."
            variant="mia"
            collapsible
            collapsed={evidenceCollapsed}
            onCollapsedChange={setEvidenceCollapsed}
            addEvidenceContext={
              client?.id && user?.sub && currentChatId
                ? {
                    areaId: currentChatId,
                    questionId: currentChatId,
                    linkType: "Analyse",
                    clientId: client.id,
                    userSub: user.sub,
                    contextLabel: selectedChat?.title ?? "Analyse chat",
                    onEvidenceAdded: handleEvidenceAdded,
                    onUploadedFilesSelected: handleUploadedFilesSelected,
                    onUploadedFilesChanged: refreshChatEvidence,
                    onSavedToLibrary: handleSavedToLibrary,
                    onUploadConversationFile: handleUploadEvidence,
                    uploadingConversationFile: uploadingEvidence,
                    uploadsConversationId: currentChatId,
                    subjectNoun: "chat",
                  }
                : undefined
            }
          />
          <AnalyseChatBar
            editable={canSendMessage}
            isStreaming={isStreaming}
            onSend={getAnalyseResponse}
            onStop={stopAnalyseResponse}
          />
          <PillsContainer items={[
            { id: "4", label: "Summarise diagnostics", fullLabel: "Summarise the diagnostics selected" },
            { id: "5", label: "Summarise strengths", fullLabel: "What are the main areas of strength identified in the diagnostics selected" },
            { id: "6", label: "Highlight improvements", fullLabel: "What are the main areas of improvement or risk highlighted in the diagnostics selected" },
            { id: "8", label: "Staff development priorities", fullLabel: "What are the priorities for staff development identified in the diagnostics selected and how can we provide that support" },
            { id: "9", label: "Summarise grade profile", fullLabel: "Summarise the grade profile across the diagnostics selected" },
            { id: "3", label: "Summarise CPD", fullLabel: "Summarise CPD" },
          ]} onClick={onPillClicked} />
        </div>
      </section>

      <section className={styles.right}>
        <AnalyseSectionsDrawer
          sectionsOnly={true}
          sections={sectionsWithChatLinks}
          archivedSections={archivedSectionsWithChatLinks}
          basePath={"/analyse"}
          activeSectionId={currentChatId}
          activeItemId={currentChatId}
          contextMenuItems={contextMenuItems}
          archivedContextMenuItems={archivedContextMenuItems}
          toolbar={
             <div className={styles.toolbar}>
              <div className={styles.toolbarActions}>
                <button 
                  onClick={newChatHandler} 
                  className={styles.newChatButton}
                  aria-label="Start new chat"
                  data-testid="analyse-new-chat"
                >
                  <div className={styles.newChatIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      <path d="M13 8H7"/>
                      <path d="M17 12H7"/>
                    </svg>
                  </div>
                  <span className={styles.newChatText}>New Chat</span>
                </button>
                <div className={styles.settingsMenuWrap} ref={settingsMenuRef}>
                  <button
                    type="button"
                    className={styles.settingsButton}
                    aria-label="Chat settings"
                    aria-haspopup="menu"
                    aria-expanded={settingsMenuOpen}
                    data-testid="analyse-chat-settings"
                    onClick={() => setSettingsMenuOpen((open) => !open)}
                  >
                    <img src="/img/gear.svg" alt="" />
                  </button>
                  {settingsMenuOpen && (
                    <div className={styles.settingsMenu} role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.settingsMenuItemArchive}
                        disabled={isArchivingChats || isDeletingChat || chats.length === 0}
                        onClick={() => {
                          setSettingsMenuOpen(false);
                          setConfirmArchiveAllChats(true);
                        }}
                      >
                        <span>Archive all chats</span>
                        <img src="/img/archive.svg" alt="" />
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.settingsMenuItem}
                        disabled={isDeletingChat || isArchivingChats || (chats.length === 0 && archivedChats.length === 0)}
                        onClick={() => {
                          setSettingsMenuOpen(false);
                          setConfirmDeleteAllChats(true);
                        }}
                      >
                        <span>Delete all chats</span>
                        <img src="/img/trash-red.svg" alt="" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.chatSearch}>
                <input
                  type="search"
                  className={styles.chatSearchInput}
                  placeholder="Search chats…"
                  value={chatSearchQuery}
                  onChange={(e) => setChatSearchQuery(e.target.value)}
                  aria-label="Search chats"
                  data-testid="analyse-chat-search"
                />
                {chatSearchLoading && (
                  <div
                    className={styles.chatSearchProgress}
                    role="status"
                    aria-live="polite"
                    data-testid="analyse-chat-search-progress"
                  >
                    <div className={styles.chatSearchProgressHeader}>
                      <span className={styles.chatSearchOrb} aria-hidden="true">
                        <span className={styles.chatSearchOrbPulse} />
                        <span className={styles.chatSearchOrbRing} />
                        <img
                          className={styles.chatSearchOrbLogo}
                          src="/img/logo.png"
                          alt=""
                        />
                      </span>
                      <div className={styles.chatSearchProgressCopy}>
                        <span className={styles.chatSearchProgressTitle}>Searching chats</span>
                        <div className={styles.chatSearchProgressTrack}>
                          <div className={styles.chatSearchProgressBar} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!chatSearchLoading && chatSearchError && (
                  <span className={styles.chatSearchError} role="alert">
                    {chatSearchError}
                  </span>
                )}
                {!chatSearchLoading && !chatSearchError && chatSearchQuery.trim() && chatSearchResults?.length === 0 && (
                  <span className={styles.chatSearchStatus} aria-live="polite">
                    No strong matches — showing usual order
                  </span>
                )}
              </div>
            </div>
          }
        />
      </section>

      {messagesCount === 0 && (
        <SelectedDiagnosticsCounter 
          categories={allVisibleCategories} 
          onDiagnosticToggle={selectedDiagnosticChanged}
        />
      )}

      {summaryPopup &&
        createPortal(
          <div
            className={styles.chatSummaryOverlay}
            onClick={() => setSummaryPopup(null)}
            role="presentation"
          >
            <div
              className={styles.chatSummaryPopup}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="analyse-chat-summary-title"
            >
              <button
                type="button"
                className={styles.chatSummaryClose}
                onClick={() => setSummaryPopup(null)}
                aria-label="Close summary"
              >
                <img src="/img/close.svg" alt="" />
              </button>
              <div className={styles.chatSummaryHeader}>
                <span className={styles.chatSummaryOrb} aria-hidden>
                  <img src="/img/logo.png" alt="" />
                </span>
                <div className={styles.chatSummaryTitles}>
                  <p className={styles.chatSummaryEyebrow}>Chat summary</p>
                  <h2 id="analyse-chat-summary-title" className={styles.chatSummaryTitle}>
                    {summaryPopup.title}
                  </h2>
                </div>
              </div>
              {summaryPopup.summary ? (
                <p className={styles.chatSummaryBody}>{summaryPopup.summary}</p>
              ) : (
                <p className={styles.chatSummaryEmpty}>
                  Mia hasn&apos;t written a summary for this chat yet. Summaries appear after the
                  first response.
                </p>
              )}
            </div>
          </div>,
          document.body
        )}
      <ConfirmAction
        isVisible={confirmArchiveAllChats}
        onClose={() => setConfirmArchiveAllChats(false)}
        clickHandler={archiveAllChatsHandler}
        title="Archive all chats?"
        message="This will archive every chat in your list. You can unarchive them later from Archived."
        confirmText="Archive all chats"
        cancelText="Cancel"
        severity="warning"
        icon="/img/archive.svg"
        loading={isArchivingChats}
      />
      <ConfirmAction
        isVisible={confirmDeleteAllChats}
        onClose={() => setConfirmDeleteAllChats(false)}
        clickHandler={deleteAllChatsHandler}
        title="Delete all chats?"
        message="This will permanently delete every chat in this list, including archived chats. This cannot be undone."
        confirmText="Delete all chats"
        cancelText="Cancel"
        severity="danger"
        icon="/img/trash-red.svg"
        loading={isDeletingChat}
      />
      <ResourceViewer
        resource={viewerResource}
        clientId={client?.id ?? ""}
        onClose={() => setViewerResource(null)}
      />
    </div>
  );
};

export default Analyse;
